import { useState, useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Parallax } from "react-scroll-parallax";
import { m } from "framer-motion";
import { fadeIn } from "../../Functions/GlobalAnimations";
import config from "../../config";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from 'react-i18next';
import ImageWithAuth from "../../Components/ImageWithAuth";

const DocumentDetails = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const documentid = location.pathname.split("/").pop();
  const { t, i18n } = useTranslation();
  
  // Déterminer la direction en fonction de la langue
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';
  
  const [document, setDocument] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${config.SITE_API_URL}/documentbyid/${documentid}`, {
          headers: {
            'x-api-key': config.SITE_API_KEY,
            'Content-Type': 'application/json',
          },
          params: { lang: i18n.language },
        });
        setDocument(response?.data?.result || {});
      } catch (error) {
        console.error("Error fetching document:", error);
        toast.error(t('document_details.fetch_error'));
      } finally {
        setIsLoading(false);
      }
    };
    fetchDocument();
  }, [documentid, i18n.language]);

  // Fonction pour extraire le nom dans la langue courante
  const getName = (obj, field = 'name') => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return obj[field]?.[i18n.language] || obj[field]?.fr || obj[field] || '';
  };

  // Fonction pour dédupliquer et formater les niveaux
  const getFormattedLevels = () => {
    if (!document.level || !Array.isArray(document.level)) return [];
    
    // Dédupliquer les noms de niveaux
    const uniqueLevels = [];
    const seenLevels = new Set();
    
    document.level.forEach(level => {
      const levelName = getName(level);
      if (levelName && !seenLevels.has(levelName)) {
        seenLevels.add(levelName);
        uniqueLevels.push(levelName);
      }
    });
    
    return uniqueLevels;
  };

  // Fonction pour dédupliquer et formater les sous-niveaux
  const getFormattedSubLevels = () => {
    if (!document.sub_level || !Array.isArray(document.sub_level)) return [];
    
    // Dédupliquer les noms de sous-niveaux
    const uniqueSubLevels = [];
    const seenSubLevels = new Set();
    
    document.sub_level.forEach(sub => {
      const subName = getName(sub);
      if (subName && !seenSubLevels.has(subName)) {
        seenSubLevels.add(subName);
        uniqueSubLevels.push(subName);
      }
    });
    
    return uniqueSubLevels;
  };

  // Calculate discount
  const discount = document.taux_discount ? document.taux_discount : 0;
  const finalPrice = document.price_ttc || 0;
  const originalPrice = discount > 0 ? finalPrice / (1 - discount / 100) : finalPrice;

  // Handle picture field
  const pictureUrl = document.picture
    ? `${config.SITE_API_URL}${document.picture}`
    : '/img/file-20210323-14-1u3ei2y.avif'; // Image par défaut

  // Fonction pour ajouter une story au panier
  const handleAddToBasket = async (documentId) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error(t('checkout.errors.login_required'));
      navigate('/login');
      return;
    }

    try {
      const response = await axios.post(
        `${config.SITE_API_URL}/add-to-basket`,
        { documentId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY,
          },
        }
      );

      // Succès normal
      toast.success(response.data.message);

    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.restricted) {
        // Message spécifique quand bloqué par abonnement non compatible
        toast.warning(error.response.data.message);
      } else if (error.response?.status === 400 && error.response.data.message.includes("déjà dans votre panier")) {
        toast.info(error.response.data.message);
      } else {
        toast.error(error.response?.data?.message || t('document_details.add_to_cart_error'));
      }
    }
  };

  // Si chargement
  if (isLoading) {
    return (
      <div style={{...props.style, direction: direction}} className="min-h-screen bg-gray-50">
        <Container className="py-20">
          <Row className="justify-center">
            <Col md={8} className="text-center">
              <div className="animate-pulse">
                <div className="h-12 bg-gray-200 rounded w-1/4 mx-auto mb-6"></div>
                <div className="h-64 bg-gray-200 rounded mb-6"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2 mx-auto"></div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Si document non trouvé
  if (!document._id) {
    return (
      <div style={{...props.style, direction: direction}} className="min-h-screen bg-gray-50">
        <ToastContainer position="bottom-left" />
        <Container className="py-20">
          <Row className="justify-center">
            <Col md={8} className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('document_details.not_found_title')}
              </h2>
              <p className="text-gray-600 mb-6">
                {t('document_details.not_found_message')}
              </p>
              <Link
                to="/course"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                {t('document_details.browse_documents')}
              </Link>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  const formattedLevels = getFormattedLevels();
  const formattedSubLevels = getFormattedSubLevels();

  return (
    <div style={{...props.style, direction: direction}}>
      <ToastContainer position="bottom-left" />
      
      <m.div className="h-[380px] overflow-hidden sm:h-[365px] relative" {...fadeIn}>
        <Parallax
          className="lg-no-parallax bg-cover absolute top-0 left-0 w-full h-full"
          translateY={[-40, 40]}
          style={{ backgroundImage: `url(/assets/img/webp/portfolio-bg.webp)` }}
        />
        <Container className="h-full relative">
          <Row className="items-stretch justify-center h-[450px] md:h-[375px] sm:h-[350px]">
            <Col xl={6} lg={7} md={8} xs={12} className="text-center flex justify-center flex-col">
              <h2 className="text-blue-900 mb-[0px] font-serif font-medium -tracking-[1px] my-auto xs:mb-[0px]">
                {t('document_details.page_title')}
              </h2>
            </Col>
          </Row>
        </Container>
      </m.div>

      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-[90%] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-gray-600 hover:text-gray-900" title={t('document_details.back_home')}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <nav className="text-sm text-gray-500" aria-label={t('document_details.breadcrumb_label')}>
                <Link to="/" aria-label={t('document_details.home_link')}>
                  {t('document_details.home')}
                </Link>
                <span className="mx-2" aria-hidden="true">/</span>
                <Link to="/course" aria-label={t('document_details.documents_link')}>
                  {t('document_details.documents')}
                </Link>
                <span className="mx-2" aria-hidden="true">/</span>
                <span className="text-gray-900" aria-label={t('document_details.current_document')}>
                  {getName(document, 'title') || t('document_details.untitled')}
                </span>
              </nav>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[90%] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 min-lg:grid-cols-2 gap-8 mb-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="relative bg-white rounded-lg shadow-sm overflow-hidden">
              <ImageWithAuth
                src={pictureUrl}
                alt={getName(document, 'title') || t('document_details.document_image_alt')}
                className="w-full h-96 object-cover"
                onError={(e) => {
                  e.target.src = '/img/file-20210323-14-1u3ei2y.avif';
                }}
              />
              {document.news && (
                <span 
                  className="absolute top-4 left-4 bg-yellow-400 text-yellow-900 text-sm px-3 py-1 rounded-full font-medium"
                  aria-label={t('document_details.new_badge')}
                >
                  {t('document_details.new')}
                </span>
              )}
              {document.best_seller && (
                <span 
                  className="absolute top-4 right-4 bg-red-500 text-white text-sm px-3 py-1 rounded-full font-medium"
                  aria-label={t('document_details.best_seller_badge')}
                >
                  {t('document_details.best_seller')}
                </span>
              )}
              {discount > 0 && (
                <span 
                  className="absolute bottom-4 left-4 bg-green-500 text-white text-sm px-3 py-1 rounded-full font-medium"
                  aria-label={t('document_details.discount_badge', { discount: Math.round(discount) })}
                >
                  -{Math.round(discount)}%
                </span>
              )}
            </div>
          </div>

          {/* Document Information */}
          <div className="space-y-6">
            <div>
              {/* Badges pour niveaux et sous-niveaux */}
              <div className="flex flex-wrap gap-2 mb-4">
                {/* Niveaux */}
                {formattedLevels.map((level, index) => (
                  <span 
                    key={`level-${index}`}
                    className="text-sm text-blue-600 font-medium bg-blue-50 px-3 py-1 rounded-full"
                  >
                    {level}
                  </span>
                ))}
                
                {/* Sous-niveaux */}
                {formattedSubLevels.map((subLevel, index) => (
                  <span 
                    key={`sublevel-${index}`}
                    className="text-sm text-indigo-600 font-medium bg-indigo-50 px-3 py-1 rounded-full"
                  >
                    {subLevel}
                  </span>
                ))}
                
                {/* Matière */}
                {document.matiere && (
                  <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                    {getName(document.matiere)}
                  </span>
                )}
                
                {/* Type de contenu */}
                {document.content?.type && (
                  <span className="text-sm text-red-600 bg-red-50 px-3 py-1 rounded-full">
                    {getName(document.content.type)}
                  </span>
                )}
                
                {/* Trimestre */}
                {document.quarter && (
                  <span className="text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full">
                    {getName(document.quarter)}
                  </span>
                )}
              </div>
              
              <h1 className="text-[25px] font-bold text-gray-900 mb-3">
                {getName(document, 'title')}
              </h1>
              <p className="text-lg text-gray-600">
                {getName(document, 'description')}
              </p>
            </div>

            {/* Price_ttc */}
              
              {/* Quantity and Purchase */}
              <div className="space-y-4">
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    handleAddToBasket(document._id);
                  }} 
                  className="w-full bg-blue-600 text-white py-3 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors"
                  aria-label={t('document_details.add_to_cart', { price: finalPrice.toFixed(3) })}
                >
                  {t('document_details.add_to_cart_button', { price: finalPrice.toFixed(3) })}
                </button>
              </div>

            {/* Technical Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('document_details.technical_info_title')}
              </h3>
              <div className="grid grid-cols-1 min-sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">{t('document_details.reference')} :</span>
                  <span className="ml-2 font-medium">{document.reference}</span>
                </div>
                <div>
                  <span className="text-gray-600">{t('document_details.published')} :</span>
                  <span className="ml-2 font-medium">
                    {document.date ? 
                      new Date(document.date).toLocaleDateString(i18n.language === 'ar' ? 'ar-TN' : 'fr-FR') : 
                      t('document_details.na')
                    }
                  </span>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentDetails;